import React, { useState, useEffect } from 'react';
import { currenciesAPI } from '../api/client';
import apiClient from '../api/client';
import { Star, Plus, AlertTriangle, Check, X, Search } from 'lucide-react';
import SortableTh from '../components/SortableTh';
import TablePagination from '../components/TablePagination';

const CUR_PAGE_SIZE = 25;

export default function CurrenciesSettings() {
  const [currencies, setCurrencies] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [settingDefault, setSettingDefault] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    currencyCode: '',
    currencyName: '',
    symbol: ''
  });
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [curPage, setCurPage] = useState(1);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(null), 4000); return () => clearTimeout(t); }
  }, [success]);

  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(null), 6000); return () => clearTimeout(t); }
  }, [error]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [currRes, defaultRes] = await Promise.all([
        currenciesAPI.getAll(),
        apiClient.get('/currencies/company-default'),
      ]);
      const currenciesList = Array.isArray(currRes) ? currRes : (currRes.data?.currencies || currRes.data || []);
      setCurrencies(currenciesList);
      setDefaultCurrency(defaultRes.data.defaultCurrency || '');
      setCompanyName(defaultRes.data.companyName || '');
    } catch (err) {
      console.error('Fetch currencies error:', err);
      setError('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({ currencyCode: '', currencyName: '', symbol: '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await currenciesAPI.create(formData);
      setSuccess('Currency added successfully');
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Add currency error:', err);
      setError(err.response?.data?.message || 'Failed to add currency');
    }
  };

  const toggleActive = async (code, currentStatus) => {
    if (code === defaultCurrency && currentStatus) {
      setError('Cannot deactivate the default currency. Set another currency as default first.');
      return;
    }
    try {
      const currency = currencies.find(c => c.CurrencyCode === code);
      await currenciesAPI.update(code, {
        currencyName: currency.CurrencyName,
        symbol: currency.Symbol,
        isActive: !currentStatus
      });
      fetchData();
    } catch (err) {
      console.error('Toggle currency error:', err);
      setError(err.response?.data?.message || 'Failed to update currency');
    }
  };

  const handleSetDefault = async (code) => {
    try {
      setSettingDefault(code);
      await apiClient.put('/currencies/company-default', { defaultCurrency: code });
      setDefaultCurrency(code);
      setSuccess(`Default currency changed to ${code}. Remember to update exchange rates.`);
    } catch (err) {
      console.error('Set default error:', err);
      setError(err.response?.data?.error || 'Failed to set default currency');
    } finally {
      setSettingDefault(null);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedCurrencies = [...currencies].sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (typeof aVal === 'boolean') return (aVal === bVal ? 0 : aVal ? -1 : 1) * dir;
    if (typeof aVal === 'string') return aVal.localeCompare(bVal) * dir;
    return ((aVal > bVal ? 1 : aVal < bVal ? -1 : 0)) * dir;
  });
  const filteredCurrencies = sortedCurrencies.filter(c => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (c.CurrencyCode && c.CurrencyCode.toLowerCase().includes(t)) ||
           (c.CurrencyName && c.CurrencyName.toLowerCase().includes(t)) ||
           (c.Symbol && c.Symbol.toLowerCase().includes(t));
  });
  const pagedCurrencies = filteredCurrencies.slice((curPage - 1) * CUR_PAGE_SIZE, curPage * CUR_PAGE_SIZE);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Currencies</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage available currencies and set your company's default reporting currency
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Currency
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-green-800">{success}</p>
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-400 hover:text-green-600"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Default Currency Info */}
      {defaultCurrency && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Star className="w-5 h-5 text-blue-600 fill-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">Default Reporting Currency</p>
                <p className="text-lg font-bold text-blue-900">
                  {defaultCurrency}
                  <span className="text-sm font-normal text-blue-700 ml-2">
                    ({currencies.find(c => c.CurrencyCode === defaultCurrency)?.CurrencyName || ''})
                  </span>
                </p>
              </div>
            </div>
            <span className="text-3xl">
              {currencies.find(c => c.CurrencyCode === defaultCurrency)?.Symbol || ''}
            </span>
          </div>
          <p className="text-xs text-blue-600 mt-2">
            All financial reports, budgets, and invoice conversions use this currency. Click the star icon on any active currency below to change it.
          </p>
        </div>
      )}

      {/* Currencies Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-700">Configured Currencies</h2>
            <p className="text-xs text-gray-400 mt-0.5">{companyName}</p>
          </div>
          <div className="relative w-60">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurPage(1); }}
              placeholder="Search currencies…"
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/40">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Default</th>
                <SortableTh field="CurrencyCode" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Code</SortableTh>
                <SortableTh field="CurrencyName" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Currency Name</SortableTh>
                <SortableTh field="Symbol" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Symbol</SortableTh>
                <SortableTh field="IsActive" sortBy={sortField} sortDir={sortDir} onSort={handleSort} align="center">Status</SortableTh>
                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pagedCurrencies.map(currency => {
                const isDefault = currency.CurrencyCode === defaultCurrency;
                return (
                  <tr key={currency.CurrencyCode} className={`hover:bg-gray-50/50 ${isDefault ? 'bg-blue-50/30' : ''}`}>
                    {/* Default Star */}
                    <td className="px-5 py-3">
                      {isDefault ? (
                        <span className="inline-flex items-center gap-1 text-blue-600" title="Default currency">
                          <Star className="w-5 h-5 fill-blue-500 text-blue-500" />
                        </span>
                      ) : currency.IsActive ? (
                        <button
                          onClick={() => handleSetDefault(currency.CurrencyCode)}
                          disabled={settingDefault === currency.CurrencyCode}
                          className="inline-flex items-center text-gray-300 hover:text-blue-500 transition-colors disabled:opacity-50"
                          title={`Set ${currency.CurrencyCode} as default`}
                        >
                          <Star className={`w-5 h-5 ${settingDefault === currency.CurrencyCode ? 'animate-pulse text-blue-400' : ''}`} />
                        </button>
                      ) : (
                        <span className="text-gray-200"><Star className="w-5 h-5" /></span>
                      )}
                    </td>
                    {/* Code */}
                    <td className="px-5 py-3">
                      <span className={`text-sm font-semibold ${isDefault ? 'text-blue-700' : 'text-gray-900'}`}>
                        {currency.CurrencyCode}
                      </span>
                    </td>
                    {/* Name */}
                    <td className="px-5 py-3 text-sm text-gray-600">
                      {currency.CurrencyName}
                    </td>
                    {/* Symbol */}
                    <td className="px-5 py-3 text-lg text-gray-600">
                      {currency.Symbol}
                    </td>
                    {/* Status */}
                    <td className="px-5 py-3 text-center">
                      {currency.IsActive ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          Inactive
                        </span>
                      )}
                    </td>
                    {/* Actions */}
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => toggleActive(currency.CurrencyCode, currency.IsActive)}
                        disabled={isDefault && currency.IsActive}
                        className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border ${
                          currency.IsActive
                            ? isDefault
                              ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                              : 'border-red-200 text-red-700 bg-red-50 hover:bg-red-100'
                            : 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100'
                        }`}
                        title={isDefault && currency.IsActive ? 'Cannot deactivate default currency' : ''}
                      >
                        {currency.IsActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <TablePagination page={curPage} pageSize={CUR_PAGE_SIZE} total={filteredCurrencies.length} onPageChange={setCurPage} />

          {currencies.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No currencies found. Click "Add Currency" to get started.
            </div>
          )}
        </div>
      </div>

      {/* Tip */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-medium">After changing the default currency:</p>
          <ol className="list-decimal list-inside mt-1 space-y-0.5 text-amber-700">
            <li>Go to Exchange Rates and click "Fetch Latest Rates"</li>
            <li>Review any pending foreign currency invoices</li>
          </ol>
        </div>
      </div>

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Currency</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.currencyCode}
                  onChange={(e) => setFormData({ ...formData, currencyCode: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., USD"
                  maxLength="10"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.currencyName}
                  onChange={(e) => setFormData({ ...formData, currencyName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., US Dollar"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Symbol <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., $"
                  maxLength="5"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                >
                  Add Currency
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
